import { useState, type InputHTMLAttributes } from 'react';
import { useFieldControlProps } from './field';
import { Icon } from './Icon';
import './styles/input.css';
import './styles/password-strength.css';

// PASSWORD INPUT — an <input> with a show/hide eye toggle. Used on EVERY password field
// (login, signup, reset, invite-accept, change-password) so the behaviour is identical
// everywhere. The reveal state is local and never leaves the component.
export function PasswordInput({
  invalid,
  className,
  disabled,
  readOnly,
  id,
  required,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: { invalid?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [show, setShow] = useState(false);
  const fieldProps = useFieldControlProps({
    id,
    invalid,
    disabled,
    readOnly,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
  });
  const isInvalid = fieldProps.invalid ?? invalid;
  const isDisabled = fieldProps.disabled ?? disabled;
  const isReadOnly = fieldProps.readOnly ?? readOnly;

  return (
    <div className="cev-password-input">
      <input
        {...rest}
        id={fieldProps.id}
        type={show ? 'text' : 'password'}
        required={fieldProps.required}
        disabled={isDisabled}
        readOnly={isReadOnly}
        aria-describedby={fieldProps['aria-describedby']}
        aria-invalid={fieldProps['aria-invalid']}
        aria-required={fieldProps['aria-required']}
        className={['cev-input', isInvalid && 'cev-input--invalid', className].filter(Boolean).join(' ')}
      />
      <button
        type="button"
        className="cev-password-input__toggle"
        aria-label={show ? 'Hide password' : 'Show password'}
        aria-pressed={show}
        disabled={isDisabled}
        onClick={() => setShow((s) => !s)}
      >
        <Icon name={show ? 'eye-off' : 'eye'} size={18} />
      </button>
    </div>
  );
}
PasswordInput.displayName = 'cevopPasswordInput';

/** Minimum score accepted by signup / reset / change-password UIs (Okay or Strong). */
export const PASSWORD_MIN_SCORE = 2 as const;

// 0–3 by length + character-class variety. Server enforces length ≥8 only (no composition
// rules). Clients gate submit at PASSWORD_MIN_SCORE so "Okay" or better is required in UI.
export function scorePassword(pw: string): 0 | 1 | 2 | 3 {
  if (pw.length < 8) return 0;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(pw)).length;
  if (pw.length >= 12 || classes >= 3) return 3;
  if (classes >= 2) return 2;
  return 1;
}

export function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const score = scorePassword(password);
  const labels = ['Too short', 'Weak', 'Okay', 'Strong'] as const;
  return (
    <div className={`cev-password-strength cev-password-strength--score-${score}`} aria-live="polite">
      <div className="cev-password-strength__bars">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={['cev-password-strength__bar', i < score && 'cev-password-strength__bar--on'].filter(Boolean).join(' ')}
          />
        ))}
      </div>
      <div className="cev-password-strength__label">{labels[score]}</div>
    </div>
  );
}
